import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import {
  CaseCreationProperties,
  ComplexValue,
  createCaseSchemaDataDefinitionModal,
  SimpleValue,
  CaseCreationContextParams,
  Value,
  CreateCaseSchemaDataDefinition,
} from '../../CreateCaseSchema';
import { AuthContext, IAuthContext } from 'src/state/providers/AuthProvider';
import { addReferralInformationItem } from 'src/state/slice/createCaseSlice';
import { useAppDispatch } from 'src/state/store/hooks';

import { UserStaticInfoContext } from '../context/UserStaticInfoContext';
import get from 'lodash/get';
import { PageContext } from 'src/state/providers/PageProvider';
import { PAGE_NAME } from 'src/constants/GlobalConstants';

const UserInformationTileLayout = styled.div`
  color: ${({ theme }) => theme.colors.Tarpon};
  font-size: ${({ theme }) => theme.fontSize.s};
  line-height: ${({ theme }) => theme.lineHeight.xl};
`;
const UserInformationTileHeader = styled.div`
  font-weight: bold;
`;
const UserInformationTileContent = styled.p`
  font-size: ${({ theme }) => theme.fontSize.s};
`;

/**
 * Responsible to calculating attribute value for Create Case.
 * The value of an attribute is defined by the executionFunction in case of ComplexValue
 * or the value provided directly in case of simple value.
 *
 * @param valueId contains SimpleValue or ComplexValue.
 * @param authContext AuthContext
 * @returns calculated value of the attribute based on the execution function.
 */
export function calculateAttributeValueForCreateCasePage(
  valueId: Value,
  authContext: IAuthContext,
) {
  return (valueId as ComplexValue).complex
    ? calculateComplexValue(
        valueId as ComplexValue,
        authContext,
        createCaseSchemaDataDefinitionModal,
      )
    : (valueId as SimpleValue).simple;
}

/**
 * Caculates the value as per the view case page.
 * Here the value is calculated based on the userStaticInfoContext value recieved from the case.
 * @param attribute the key of the attribute
 * @param userStaticInfoContext the object retrieved from the case referral information section.
 * @returns the string value.
 */
export function calculateAttributeValueForViewCasePage(
  attribute: string,
  userStaticInfoContext: { [attribute: string]: string },
): string {
  return get(userStaticInfoContext, attribute);
}

/**
 * Responsible to calculate the Complex Value.
 * @param value
 * @param authContext
 * @param createCaseSchemaDataDefinitionModal
 * @returns calculated complex value by function execution.
 */
export const calculateComplexValue = (
  value: ComplexValue,
  authContext: IAuthContext,
  createCaseSchemaDataDefinitionModal: CreateCaseSchemaDataDefinition,
): string => {
  const execFn = get(createCaseSchemaDataDefinitionModal, value.complex) as (
    userDetails: CaseCreationContextParams,
  ) => string;
  return execFn(authContext);
};

/**
 * Calcuates the value for the {attribute}.
 * It first checks if the value can be hydrated from the parameters provided. If yes, then the value is resurned.
 * If no value is provided after hydration, then either simpleValue is returned or complex value is returned.
 *
 * @param attribute the key.
 * @param valueId contains the SimpleValue or ComplexValue.
 * @param userStaticInfoContext Object from where the value can be hydrated. ideally used for View case page.
 * @param authContext AuthContext
 * @returns the calculated value of the {attribute}.
 */

/**
 * UserInformationTileProps for fetching the key and componsite values for the tile.
 * attribute is the key
 * properties contains the composite values.
 * Eg: for the below JSON:
 * fullName: {
      label: 'Full Name',
      value: {
        complex: 'fullName',
      },
    }
 * attribute is fullName
 * value is composed of the value part for fullName
 */
interface UserInformationTileProps {
  attribute: string;
  properties: CaseCreationProperties;
}

/**
 * UserInformationTile component is responsible to render the item on the screen.
 * @param UserInformationTileProps
 * @returns UserInformationTile.
 */
const UserInformationTile: React.FC<UserInformationTileProps> = ({
  attribute,
  properties,
}) => {
  const authContext = useContext(AuthContext);
  const userStaticInfoContext = useContext(UserStaticInfoContext);
  const dispatch = useAppDispatch();
  const { pageName } = useContext(PageContext);

  const value =
    PAGE_NAME.CASE_SUMMARY == pageName
      ? calculateAttributeValueForViewCasePage(attribute, userStaticInfoContext)
      : calculateAttributeValueForCreateCasePage(properties.value, authContext);

  useEffect(() => {
    dispatch(addReferralInformationItem({ attribute, value }));
  }, []);

  return (
    <UserInformationTileLayout>
      <UserInformationTileHeader>{properties.label}</UserInformationTileHeader>
      <UserInformationTileContent data-testid={attribute}>
        {value}
      </UserInformationTileContent>
    </UserInformationTileLayout>
  );
};

export default UserInformationTile;
